// Базовые типы
export type MindboxStatus =
  | 'Success'
  | 'ValidationError'
  | 'ProtocolError'
  | 'InternalServerError';

export type MindboxBaseResponse = {
  status: MindboxStatus;
};

export type MindboxError = {
  status: Exclude<MindboxStatus, 'Success'>;
  message?: string;
};

export const MINDBOX_OPERATIONS = {
  AUTHORIZE_CUSTOMER: 'Website.AuthorizeCustomer',
  VIEW_PRODUCT: 'Website.ViewProduct',
  VIEW_CATEGORY: 'Website.ViewCategory',
  CART_CREATE: 'Website.SetCart',
  CART_CLEAR: 'Website.ClearCart',
  FAVORITES_CREATE: 'Website.SetWishList',
  FAVORITES_CLEAR: 'Website.ClearWishList',
  SUBSCRIPTION: 'Website.Subscription',
  SUBSCRIBE_CUSTOMER_IN_FOOTER: 'Website.SubscriptionInFooter',

  GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL: 'ProverkaNalichiyaPodpiski',
  GET_CUSTOMER_SUBSCRIPTIONS: 'Website.GetCustomerSubscriptions',
  GET_PRODUCT_SUBSCRIPTIONS: 'PodpiskaNaProduktNeVNalichii',
  CATEGORY_POPULAR_PRODUCTS: 'Categorypopularproducts',
  PERSONAL_RECOMMENDATIONS: 'RealtimePersonalRecommendations',
  RELATED_PRODUCTS: 'Relatedproducts',
  POPULAR_PRODUCTS: 'PopularProducts',
  SIMILAR_PRODUCTS: 'Similarproducts',
  PRODUCT_RECOMMENDATIONS: 'Website.ProductRecommendations',
  PRODUCT_RECOMMENDATIONS_BY_CATEGORY:
    'Website.ProductRecommendationsByCategory',
  GET_CUSTOMER_LOYALTY_LEVEL: 'Website.GetCustomerLoyaltyLevel',
  GET_CUSTOMER_INFO: 'Website.CheckCustomer',
  GET_CUSTOMER_BALANCE_HISTORY: 'Website.GetCustomerBonusPointsHistory',
  CREATE_REVIEW: 'OstavilOtzyvNaSajte',
  CALCULATE_CATALOG_PRICES_AUTHORIZED:
    'Website.CalculateCatalogPricesAuthorized',
  CALCULATE_CATALOG_PRICES_ANONIM: 'Website.CalculateCatalogPricesAnonim'
} as const;

// Тип операции на основе значений из MINDBOX_OPERATIONS
export type MindboxOperationType =
  (typeof MINDBOX_OPERATIONS)[keyof typeof MINDBOX_OPERATIONS];

// Базовые типы для общих полей
export type Subscription = {
  brand?: string;
  pointOfContact: string;
  topic?: string;
  isSubscribed: boolean;
};

export type CustomerBase = {
  email?: string;
  mobilePhone?: string;
  ids: {
    websiteID?: number | string;
  };
};

export type ProductListCreate = Array<{
  count: number;
  pricePerItem?: number;
  priceOfLine?: number;
  customFields?: Record<string, unknown>;
  product: {
    ids: {
      dreameSite: string;
    };
  };
  productGroup?: {
    ids: {
      dreameSite: string;
    };
  };
}>;

export type CustomerExtended = CustomerBase & {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  isEmailInvalid?: boolean;
  mobilePhone?: number;
  isMobilePhoneInvalid?: boolean;
  birthDate?: string;
  sex?: 'male' | 'female';
  changeDateTimeUtc?: string;
  ianaTimeZone?: string;
  timeZoneSource?: string;
  processingStatus?: string;
  area?: {
    name?: string;
    ids?: {
      externalId?: string;
    };
  };
  customFields?: Record<string, unknown>;
};

// Базовые типы для рекомендаций
export type RecommendationBase = {
  name: string;
  description?: string;
  displayName?: string;
  url?: string;
  pictureUrl?: string;
  price: number;
  oldPrice?: number;
  category?: string;
  vendorCode: string;
  ids: {
    mindboxId?: number;
    bitrixProducts?: string;
  };
  manufacturer?: {
    name: string;
  };
};

export type RecommendationResponse = MindboxBaseResponse & {
  recommendations: RecommendationBase[];
};

// Маппинг типов запросов
export type MindboxRequestBody = {
  [MINDBOX_OPERATIONS.AUTHORIZE_CUSTOMER]: {
    customer: CustomerBase;
  };
  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS]: {
    customer: CustomerBase;
  };

  [MINDBOX_OPERATIONS.GET_PRODUCT_SUBSCRIPTIONS]: {
    customer: {
      email: string;
      subscriptions?: Subscription[];
    };
    addProductToList: {
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.SUBSCRIPTION]: {
    customer: CustomerBase & {
      email?: string;
      subscriptions?: Subscription[];
      customFields?: Record<string, unknown>;
      ids?: {
        bitrixClientId?: string;
        websiteID?: string;
      };
    };
  };

  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL]: {
    customer: {
      email: string;
    };
  };

  [MINDBOX_OPERATIONS.VIEW_PRODUCT]: {
    viewProduct: {
      price?: number;
      product: {
        ids: {
          dreameSite: string;
        };
      };
      customerAction?: {
        customFields?: Record<string, unknown>;
      };
    };
  };

  [MINDBOX_OPERATIONS.VIEW_CATEGORY]: {
    viewProductCategory: {
      productCategory: {
        ids: {
          dreameSite: string;
        };
      };
      customerAction?: {
        customFields?: Record<string, unknown>;
      };
    };
  };

  [MINDBOX_OPERATIONS.CART_CREATE]: {
    productList: ProductListCreate;
  };

  [MINDBOX_OPERATIONS.CART_CLEAR]: {
    data?: object;
  };

  [MINDBOX_OPERATIONS.FAVORITES_CREATE]: {
    customer?: CustomerBase;
    productList: ProductListCreate;
  };

  [MINDBOX_OPERATIONS.FAVORITES_CLEAR]: {
    data?: object;
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED]: {
    customer: CustomerBase & {
      email?: string;
      mobilePhone?: string;
    };
    productList: {
      calculationDateTimeUtc?: string;
      area?: {
        ids: {
          externalId: string;
        };
      };
      items: Array<{
        basePricePerItem: string | number;
        minPricePerItem?: string | number;
        product: {
          ids: {
            bitrixProducts: string;
          };
        };
        requestedPromotions?: Array<{
          type: string;
          amount?: string;
          promotion?: {
            ids: {
              externalId: string;
            };
          };
          coupon?: {
            ids: {
              code: string;
            };
            pool?: {
              ids: {
                externalId: string;
              };
            };
          };
        }>;
      }>;
    };
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_ANONIM]: Omit<
    MindboxRequestBody[typeof MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED],
    'customer'
  > & {
    customer?: Omit<
      MindboxRequestBody[typeof MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED]['customer'],
      'ids'
    >;
  };

  [MINDBOX_OPERATIONS.CATEGORY_POPULAR_PRODUCTS]: {
    recommendation: {
      limit: number;
      area: {
        ids: {
          externalId: string;
        };
      };
      productCategory: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.PERSONAL_RECOMMENDATIONS]: {
    recommendation: {
      limit: number;
      area: {
        ids: {
          externalId: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.RELATED_PRODUCTS]: {
    recommendation: {
      limit: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.POPULAR_PRODUCTS]: {
    recommendation: {
      limit: number;
      area: {
        ids: {
          externalId: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.SIMILAR_PRODUCTS]: {
    recommendation: {
      limit: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS]: {
    recommendation: {
      limit: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS_BY_CATEGORY]: {
    recommendation: {
      limit: number;
      productCategory: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };
};

// Маппинг типов ответов
export type MindboxResponseBody = {
  [MINDBOX_OPERATIONS.AUTHORIZE_CUSTOMER]: MindboxBaseResponse;

  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS]: MindboxBaseResponse & {
    customer: CustomerExtended & {
      subscriptions: Subscription[];
    };
  };

  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL]: MindboxBaseResponse & {
    customer: CustomerExtended & {
      subscriptions: Subscription[];
    };
  };

  [MINDBOX_OPERATIONS.GET_PRODUCT_SUBSCRIPTIONS]: MindboxBaseResponse;

  [MINDBOX_OPERATIONS.SUBSCRIPTION]: MindboxBaseResponse & {
    customer: CustomerExtended & {
      subscriptions: Subscription[];
    };
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED]: MindboxBaseResponse & {
    customer?: {
      processingStatus: string;
    };
    productList: {
      processingStatus?: string;
      statusDescription?: string;
      items: Array<{
        basePricePerItem: number;
        minPricePerItem?: number;
        priceForCustomer?: number;
        priceWithDiscountsAndSpentBonusPoints?: number;
        product: {
          ids: {
            bitrixProducts: string;
          };
          promoLabels?: Array<{
            label: string;
            promotionExternalId: string;
          }>;
        };
        appliedPromotions?: Array<{
          type: string;
          groupingKey?: string;
          amount: number;
          promotion: {
            name?: string;
            type?: string;
            ids: {
              mindboxId?: number;
              externalId: string;
            };
          };
          balanceType?: { ids: { systemName: string } };
          coupon?: {
            ids: {
              code: string;
            };
            pool?: {
              name?: string;
              description?: string;
              ids: {
                mindboxId?: number;
                externalId?: string;
              };
            };
          };
          limits?: Array<{
            type: string;
            untilDateTimeUtc?: string;
            amount?: {
              type: string;
              value: number;
            };
            used?: {
              amount: number;
              usageServiceStatus: string;
            };
          }>;
        }>;
        earnBonusPoints?: Array<{
          amount: number;
          balance: {
            systemName: string;
          };
        }>;
      }>;
    };
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_ANONIM]: MindboxResponseBody[typeof MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED];

  [MINDBOX_OPERATIONS.CATEGORY_POPULAR_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.PERSONAL_RECOMMENDATIONS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.RELATED_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.POPULAR_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.SIMILAR_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS_BY_CATEGORY]: RecommendationResponse;
  [MINDBOX_OPERATIONS.GET_CUSTOMER_LOYALTY_LEVEL]: MindboxBaseResponse & {
    balances: Array<{
      total: number;
      available: number;
      blocked: number;
      nearestExpiration: {
        total: number;
        date: string;
      };
      systemName: string;
      balanceType: {
        ids: {
          systemName: string;
        };
        name: string;
      };
    }>;
    customerSegmentations: Array<{
      segmentation: {
        ids: {
          externalId: string;
        };
        name: string;
      };
      segment: {
        ids: {
          externalId: string;
        };
        name: string;
      };
    }>;
  };
  [MINDBOX_OPERATIONS.GET_CUSTOMER_INFO]: MindboxBaseResponse & {
    balances: Array<{
      total: number;
      available: number;
      blocked: number;
      nearestExpiration: {
        total: number;
        date: string;
      };
      systemName: string;
      balanceType: {
        ids: {
          systemName: string;
        };
        name: string;
      };
    }>;
    customer: {
      customFields: {
        secondLevelEndDate?: string;
        loyaltyLevel: string;
        referalCode?: string;
      };
    };
    retailOrderStatistics: {
      totalPaidAmount: number;
    };
  };
  [MINDBOX_OPERATIONS.GET_CUSTOMER_BALANCE_HISTORY]: MindboxBaseResponse & {
    customerActions: Array<{
      ids: {
        mindboxId: number;
      };
      actionTemplate: {
        systemName: string;
        name: string;
      };
      dateTimeUtc: string;
      pointOfContact: {
        ids: {
          externalId: string;
        };
      };
      customer: {
        ids: {
          mindboxId: number;
          bitrixClientId: string;
          referralCustomerCode: string;
        };
      };
      customerBalanceChanges: Array<{
        balanceType: {
          ids: {
            systemName: string;
          };
          name: string;
        };
        changeAmount: number;
        expirationDateTimeUtc: string;
        isAvailable: boolean;
        availableFromDateTimeUtc: string;
        balanceChangeKind: {
          systemName: string;
        };
      }>;
      order: {
        ids: {
          orderID: string;
        };
      };
    }>;
  };
};

// Типы для использования в API
export type ResponseSuccess<T extends MindboxOperationType> =
  T extends keyof MindboxResponseBody
    ? MindboxResponseBody[T]
    : MindboxBaseResponse;

export type ResponseError = {
  error: MindboxError;
};

export type RequestBody<T extends MindboxOperationType> =
  T extends keyof MindboxRequestBody
    ? MindboxRequestBody[T]
    : Record<string, unknown>;
